import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Image, Modal, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash } from 'react-bootstrap-icons';
import exhibitionPhotoService from '../services/exhibitionPhotoService';
import exhibitionService from '../services/exhibitionService';
import config from '../../config';

// Dosya adı temizleme fonksiyonu
const createSafeFileName = (originalFileName) => {
  // Türkçe karakterleri sil
  const turkishChars = ['ç', 'ğ', 'ı', 'ö', 'ş', 'ü', 'Ç', 'Ğ', 'I', 'İ', 'Ö', 'Ş', 'Ü'];
  let fileName = originalFileName;
  
  turkishChars.forEach(char => {
    fileName = fileName.replace(new RegExp(char, 'g'), '');
  });
  
  // Tüm özel karakterleri sil (sadece harf ve rakam bırak)
  fileName = fileName.replace(/[^a-zA-Z0-9]/g, '');
  
  return fileName;
};

const ExhibitionPhotosPage = () => {
  const { exhibitionId } = useParams();
  const navigate = useNavigate();
  const [exhibition, setExhibition] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const loadExhibition = useCallback(async () => {
    try {
      // Get exhibition details (you might need to add this endpoint)
      const exhibitionData = await exhibitionService.getExhibitionById(exhibitionId);
      setExhibition(exhibitionData);
    } catch (error) {
      console.error('Failed to load exhibition:', error);
    }
  }, [exhibitionId]);

  const loadPhotos = useCallback(async () => {
    try {
      setLoading(true);
      const data = await exhibitionPhotoService.getPhotosByExhibition(exhibitionId);
      setPhotos(data);
      setError(null);
    } catch (error) {
      console.error('Failed to load photos:', error);
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [exhibitionId]);

  useEffect(() => {
    loadExhibition();
    loadPhotos();
  }, [loadExhibition, loadPhotos]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleAddPhotos = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      console.log('Starting photo upload for exhibition:', exhibitionId);
      console.log('Selected files:', selectedFiles);

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        console.log(`Uploading file ${i + 1}/${selectedFiles.length}:`, file.name);
        console.log('File object:', file);
        console.log('File type:', typeof file);
        console.log('Is File instance:', file instanceof File);
        
        // FormData yapısını düzeltiyoruz - backend çoklu dosya dizisi bekliyor
        const formData = new FormData();
        
        // Dosyayı 'image[]' olarak gönderiyoruz (backend çoklu dosya dizisi bekliyor)
        formData.append('image[]', file);
        
        // Dosya adını temizle ve title olarak ekle
        const originalFileName = file.name.replace(/\.[^/.]+$/, ""); // Remove file extension
        const safeFileName = createSafeFileName(originalFileName);
        
        // Diğer alanları ekliyoruz
        formData.append('title', safeFileName);
        formData.append('description', '');

        console.log('Original filename:', originalFileName);
        console.log('Safe filename:', safeFileName);
        console.log('FormData for file:', formData);

        const result = await exhibitionPhotoService.addPhoto(exhibitionId, formData);
        console.log('Upload result:', result);
        
        // Update progress
        const progress = ((i + 1) / selectedFiles.length) * 100;
        setUploadProgress(progress);
      }

      // Refresh photos list
      await loadPhotos();
      
      // Reset form
      setSelectedFiles([]);
      setShowAddModal(false);
      setUploadProgress(0);
    } catch (error) {
      console.error('Failed to upload photos:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      
      const errorMessage = error.response?.data?.error || error.message;
      const logs = error.response?.data?.logs || [];
      console.log('Backend logs:', logs);
      setError(`Failed to upload some photos: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (window.confirm('Are you sure you want to delete this photo?')) {
      try {
        await exhibitionPhotoService.deletePhoto(photoId);
        await loadPhotos();
      } catch (error) {
        console.error('Failed to delete photo:', error);
        setError('Failed to delete photo');
      }
    }
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setSelectedFiles([]);
    setUploadProgress(0);
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button variant="outline-secondary" onClick={handleBack} className="me-3">
            <ArrowLeft /> Back
          </Button>
          <div>
            <h2 className="mb-0">Exhibition Photos</h2>
            {exhibition && (
              <p className="text-muted mb-0">{exhibition.title}</p>
            )}
          </div>
        </div>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <Plus /> Add New Photos
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Photos Grid */}
      {photos.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <h4 className="text-muted">No photos yet</h4>
            <p className="text-muted">Click "Add New Photos" to upload your first photo</p>
            <Button variant="primary" onClick={() => setShowAddModal(true)}>
              <Plus /> Add Photos
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Row xs={1} sm={2} md={3} lg={4} xl={5} className="g-4">
          {photos.map((photo) => (
            <Col key={photo.id}>
              <Card className="h-100 shadow-sm">
                <div className="position-relative">
                  <Image 
                    src={photo.image_url ? (() => {
                      // Eğer path zaten /api ile başlıyorsa, sadece domain ekle
                      if (photo.image_url.startsWith('/api/')) {
                        return `https://tekstas.com${photo.image_url}`;
                      }
                      // Eğer path /uploads ile başlıyorsa, /api ekleyerek düzelt
                      else if (photo.image_url.startsWith('/uploads/')) {
                        return `https://tekstas.com/api${photo.image_url}`;
                      }
                      // Diğer durumlar için API_URL ile birleştir
                      else {
                        return `${config.API_URL}${photo.image_url}`;
                      }
                    })() : null}
                    style={{ 
                      height: '250px', 
                      objectFit: 'cover',
                      width: '100%'
                    }}
                    alt={photo.title || 'Photo'}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    className="position-absolute top-0 end-0 m-2"
                    onClick={() => handleDeletePhoto(photo.id)}
                  >
                    <Trash />
                  </Button>
                </div>
                <Card.Body>
                  <Card.Title className="h6">{photo.title || 'Untitled'}</Card.Title>
                  {photo.description && (
                    <Card.Text className="small text-muted">
                      {photo.description}
                    </Card.Text>
                  )}
                  <small className="text-muted">
                    Added: {new Date(photo.created_at).toLocaleDateString()}
                  </small>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Add Photos Modal */}
      <Modal show={showAddModal} onHide={handleModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Photos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddPhotos}>
            <Form.Group className="mb-3">
              <Form.Label>Select Photos (Multiple)</Form.Label>
              <Form.Control 
                type="file" 
                onChange={handleFileSelect}
                accept="image/*"
                multiple
                required
              />
              <Form.Text className="text-muted">
                You can select multiple images at once. Supported formats: JPG, PNG, GIF
              </Form.Text>
            </Form.Group>

            {selectedFiles.length > 0 && (
              <div className="mb-3">
                <h6>Selected Files ({selectedFiles.length}):</h6>
                <ul className="list-unstyled">
                  {selectedFiles.map((file, index) => (
                    <li key={index} className="text-muted">
                      {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {uploading && (
              <div className="mb-3">
                <div className="d-flex justify-content-between">
                  <span>Uploading...</span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <div className="progress">
                  <div 
                    className="progress-bar" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddPhotos}
            disabled={selectedFiles.length === 0 || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Photos'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ExhibitionPhotosPage; 